import { Link } from "react-router-dom";
import { ArrowRight, BriefcaseBusiness, CheckCircle2, Mail, MapPin, ShieldCheck, Sparkles, Users } from "lucide-react";

const values = [
  {
    title: "Build for real operators",
    text: "We care about the people who manage inventory, client pressure, collections, and field updates every day. The product should make their work clearer and faster.",
  },
  {
    title: "Keep work honest",
    text: "We believe good software respects the truth of the business: dates, availability, payments, permissions, and reports should stay connected.",
  },
  {
    title: "Move with ownership",
    text: "Small teams do their best work when people think deeply, communicate clearly, and take responsibility for the details they touch.",
  },
  {
    title: "Learn from the market",
    text: "Outdoor media has its own rhythm. We listen to owners, sales teams, accounts teams, and field teams before deciding what should be built next.",
  },
];

const roles = [
  "Product engineering",
  "Frontend and design systems",
  "Backend and platform reliability",
  "Customer success and onboarding",
  "Sales and market development",
  "Operations and implementation",
];

function CareersNavbar() {
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-700/25">
            <span className="font-display text-lg font-black leading-none">H</span>
          </div>
          <div className="text-left">
            <div className="font-display text-lg font-black leading-none text-slate-950">Hoardexa</div>
            <div className="mt-1 text-[10px] font-black uppercase tracking-[0.28em] text-slate-500">
              Outdoor Ads
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/" className="hidden rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 sm:block">
            Home
          </Link>
          <Link to="/login" className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
            Sign in
          </Link>
        </div>
      </div>
    </header>
  );
}

function CareersFooter() {
  return (
    <footer className="bg-slate-950 py-8 text-white">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:px-6 md:flex-row lg:px-8">
        <div>
          <div className="font-display text-sm font-bold">Hoardexa</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Product by SkillEdge Technologies</div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-400">
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to="/pricing" className="hover:text-white">Pricing</Link>
          <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
          <a href="mailto:careers@hoardexa.com" className="text-white">careers@hoardexa.com</a>
        </div>
      </div>
    </footer>
  );
}

function TeamSignal() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl shadow-slate-950/10">
      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-600">Team Focus</div>
          <div className="mt-1 font-display text-xl font-black text-slate-950">Build the operating layer</div>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
          <BriefcaseBusiness size={18} />
        </div>
      </div>

      <div className="mt-5 grid gap-3">
        {[
          ["Product", "Sharper workflows for inventory, proposals, and accounts"],
          ["Customers", "Direct learning with owners and outdoor media teams"],
          ["Craft", "Clear interfaces, reliable systems, and thoughtful defaults"],
        ].map(([label, text]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-sm font-black text-slate-950">
              <CheckCircle2 size={16} className="text-emerald-600" />
              {label}
            </div>
            <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">{text}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-white">
        <MapPin size={14} />
        India focused, product first
      </div>
    </div>
  );
}

export function Careers() {
  return (
    <div className="min-h-screen bg-white text-slate-950">
      <CareersNavbar />

      <main>
        <section className="relative overflow-hidden border-b border-slate-200 bg-slate-50 pt-28">
          <div className="pointer-events-none absolute inset-0 opacity-70">
            <div className="absolute -right-20 top-10 h-80 w-80 rounded-full bg-blue-100 blur-3xl" />
            <div className="absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-emerald-100 blur-3xl" />
          </div>
          <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 pb-14 sm:px-6 lg:grid-cols-[1fr_0.78fr] lg:px-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-blue-700">
                <Sparkles size={13} /> Careers at Hoardexa
              </div>
              <h1 className="mt-5 font-display text-4xl font-black leading-[1.04] tracking-tight text-slate-950 sm:text-5xl lg:text-[58px]">
                Help outdoor media teams run cleaner, faster, better businesses.
              </h1>
              <p className="mt-5 max-w-2xl text-base font-semibold leading-8 text-slate-600">
                At Hoardexa, we believe software should reduce confusion, protect business truth, and give teams the
                confidence to move from inquiry to proposal to payment without repeated work.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href="mailto:careers@hoardexa.com"
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-700/25 transition hover:bg-blue-700"
                >
                  careers@hoardexa.com <ArrowRight size={15} />
                </a>
                <Link
                  to="/#contact"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-black text-slate-950 transition hover:border-blue-500 hover:text-blue-700"
                >
                  Learn about Hoardexa
                </Link>
              </div>
            </div>
            <TeamSignal />
          </div>
        </section>

        <section className="py-14 lg:py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <div className="text-[11px] font-black uppercase tracking-[0.24em] text-blue-600">What We Believe</div>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                Work with people who care about the problem.
              </h2>
            </div>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((item) => (
                <div key={item.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <ShieldCheck size={17} />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-black text-slate-950">{item.title}</h3>
                  <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 bg-slate-50 py-14 lg:py-16">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.24em] text-blue-600">Want To Work With Us?</div>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                Send us your interest.
              </h2>
              <p className="mt-4 text-sm font-semibold leading-7 text-slate-600">
                We are always happy to hear from thoughtful people who want to build useful tools for Indian businesses.
                Tell us what you are good at, what kind of work gives you energy, and why Hoardexa interests you.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="grid gap-3 sm:grid-cols-2">
                {roles.map((role) => (
                  <div key={role} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-black text-slate-800">
                    <Users size={16} className="shrink-0 text-blue-600" />
                    {role}
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-5">
                <div className="flex items-start gap-3">
                  <Mail size={20} className="mt-0.5 shrink-0 text-blue-600" />
                  <div>
                    <div className="font-display text-base font-black text-slate-950">No open role listed? Still write to us.</div>
                    <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">
                      Email your profile, portfolio, or a short introduction to{" "}
                      <a href="mailto:careers@hoardexa.com" className="font-black text-blue-700 hover:underline">
                        careers@hoardexa.com
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <CareersFooter />
    </div>
  );
}
